import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { descreverGlosa } from './tiss-unimed/TissGlosas';
import './PainelAdministrativo.css';
import './ConferenciaHospital.css';
import './TabelaUnimed.css';

// Tabela de preços da UNIMED no LIS (ao vivo) × o que a UNIMED paga (demonstrativos importados),
// com drill-down: exame → mês → atendimentos/glosas → atendimento. Backend:
// routes_organized/financeiro/financeiro-tabela-unimed.js

const moeda = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dec = (v, n = 2) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: n, maximumFractionDigits: n }));
const numero = (v) => Number(v || 0).toLocaleString('pt-BR');
const dataBR = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesBR = (m) => (m ? `${MESES[Number(m.slice(5, 7)) - 1]}/${m.slice(0, 4)}` : '—');

const SITUACAO = {
  mais: { label: 'Cobra a mais', cor: '#e34948', desc: 'Valor do LIS acima do que a UNIMED paga: vira glosa 1705' },
  menos: { label: 'Cobra a menos', cor: '#1f8a5b', desc: 'Valor do LIS abaixo: a UNIMED paga a tabela dela, o relatório do LIS fica menor' },
  ok: { label: 'Confere', cor: '#8a93a2', desc: 'Valor do LIS igual ao da UNIMED' },
  sem: { label: 'Sem preço UNIMED', cor: '#c0c6d0', desc: 'Código ainda não apareceu em nenhum demonstrativo importado' },
};
function situacao(e) {
  if (e.unimed == null) return 'sem';
  if (e.diferenca > 0.009) return 'mais';
  if (e.diferenca < -0.009) return 'menos';
  return 'ok';
}

function Glosas({ codigos }) {
  if (!codigos) return <span className="tu-sutil">—</span>;
  return (
    <span className="tu-glosas">
      {String(codigos).split(',').filter(Boolean).map((c) => (
        <span key={c} className={`tu-glosa ${c === '1705' ? 'tu-glosa-valor' : ''}`} title={descreverGlosa(c) || c}>{c}</span>
      ))}
    </span>
  );
}

function Diferenca({ v }) {
  if (v == null) return <span className="tu-sutil">—</span>;
  const cls = v > 0.009 ? 'tu-mais' : v < -0.009 ? 'tu-menos' : 'tu-sutil';
  return <span className={cls}>{v > 0.009 ? '+' : ''}{dec(v)}</span>;
}

export default function TabelaUnimed() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [visao, setVisao] = useState('exames');
  const [filtro, setFiltro] = useState('divergentes');
  const [soUsados, setSoUsados] = useState(true);
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState({ campo: 'glosas_1705_valor', dir: -1 });
  const [trilha, setTrilha] = useState([]); // drill: [{tipo, ...}]
  const [detalhes, setDetalhes] = useState({}); // cache por código TUSS
  const [glosasMes, setGlosasMes] = useState(null);
  const [importando, setImportando] = useState(false);
  const [importacao, setImportacao] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const [t, g] = await Promise.all([
        axios.get(`${API_URL}/financeiro/tabela-unimed`),
        axios.get(`${API_URL}/financeiro/tabela-unimed/glosas-por-mes`),
      ]);
      setDados(t.data); setGlosasMes(g.data); setDetalhes({});
    } catch (e) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setCarregando(false);
    }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const carregarDetalhe = useCallback(async (codigo, exame) => {
    const chave = codigo || `ex:${exame}`;
    if (detalhes[chave]) return;
    setDetalhes((d) => ({ ...d, [chave]: { carregando: true } }));
    try {
      const r = await axios.get(`${API_URL}/financeiro/tabela-unimed/detalhe`, { params: { codigo, exame } });
      setDetalhes((d) => ({ ...d, [chave]: r.data }));
    } catch (e) {
      setDetalhes((d) => ({ ...d, [chave]: { erro: e.response?.data?.error || e.message } }));
    }
  }, [detalhes]);

  const entrar = (no) => {
    if (no.tipo === 'codigo') carregarDetalhe(no.codigo, no.exame);
    setTrilha((t) => [...t, no]);
    // o scroll da aplicação é do .main-container, não da janela
    const el = document.querySelector('.main-container');
    if (el) el.scrollTo({ top: 0 }); else window.scrollTo({ top: 0 });
  };
  const voltarPara = (i) => setTrilha((t) => t.slice(0, i));
  const abrirAtendimento = (ref) => {
    const a = (det?.atendimentos || []).find((x) => x.local === ref.local && x.protocolo === ref.protocolo);
    if (a) entrar({ tipo: 'atendimento', atendimento: a });
  };

  async function importar(ev) {
    const arquivos = [...(ev.target.files || [])];
    ev.target.value = '';
    if (!arquivos.length) return;
    setImportando(true); setImportacao(null);
    const fd = new FormData();
    arquivos.forEach((f) => fd.append('arquivos', f));
    try {
      const r = await axios.post(`${API_URL}/financeiro/tabela-unimed/demonstrativos`, fd, { timeout: 300000 });
      setImportacao(r.data.resultado);
      await carregar();
    } catch (e) {
      setImportacao([{ ok: false, arquivo: 'envio', erro: e.response?.data?.error || e.message }]);
    } finally {
      setImportando(false);
    }
  }

  const exames = useMemo(() => {
    if (!dados) return [];
    const q = busca.trim().toUpperCase();
    let l = dados.exames.map((e) => ({ ...e, situacao: situacao(e) }));
    if (soUsados) l = l.filter((e) => e.uso_12m > 0);
    if (filtro === 'divergentes') l = l.filter((e) => e.situacao === 'mais' || e.situacao === 'menos');
    else if (filtro !== 'todos') l = l.filter((e) => e.situacao === filtro);
    if (q) l = l.filter((e) => [e.exame, e.descricao, e.codigo, e.procedimento].some((x) => String(x || '').toUpperCase().includes(q)));
    const { campo, dir } = ordem;
    return l.sort((a, b) => {
      const x = a[campo]; const y = b[campo];
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === 'string' ? x.localeCompare(y) : x - y) * dir;
    });
  }, [dados, busca, filtro, soUsados, ordem]);

  const contagem = useMemo(() => {
    const c = { mais: 0, menos: 0, ok: 0, sem: 0 };
    (dados?.exames || []).filter((e) => !soUsados || e.uso_12m > 0).forEach((e) => { c[situacao(e)] += 1; });
    return c;
  }, [dados, soUsados]);

  const meses = useMemo(() => {
    const m = new Map();
    for (const r of glosasMes || []) {
      if (!m.has(r.mes)) m.set(r.mes, { mes: r.mes, itens: 0, glosados: 0, glosas_1705: 0, valor_1705: 0, valor_outras: 0, codigos: 0 });
      const x = m.get(r.mes);
      x.itens += r.itens; x.glosados += r.glosados; x.glosas_1705 += r.glosas_1705;
      x.valor_1705 += r.valor_1705; x.valor_outras += r.valor_outras; x.codigos += 1;
    }
    return [...m.values()].sort((a, b) => b.mes.localeCompare(a.mes));
  }, [glosasMes]);

  const Th = ({ campo, children, num }) => (
    <th className={`tu-ord ${num ? 'num' : ''}`} onClick={() => setOrdem((o) => ({ campo, dir: o.campo === campo ? -o.dir : -1 }))}>
      {children}{ordem.campo === campo ? (ordem.dir < 0 ? ' ▾' : ' ▴') : ''}
    </th>
  );

  // ------------------------------------------------------------------ níveis do drill
  const topo = trilha[trilha.length - 1];
  const rotulo = (no) => {
    if (no.tipo === 'codigo') return `${no.exame || no.codigo}${no.codigo ? ` · ${no.codigo}` : ''}`;
    if (no.tipo === 'mes' || no.tipo === 'mesGlobal') return mesBR(no.mes);
    if (no.tipo === 'atendimento') return `${no.atendimento.local}/${no.atendimento.protocolo}`;
    return '';
  };
  const codigoAtual = [...trilha].reverse().find((n) => n.tipo === 'codigo');
  const det = codigoAtual ? detalhes[codigoAtual.codigo || `ex:${codigoAtual.exame}`] : null;
  const exameDaTabela = codigoAtual && dados ? dados.exames.filter((e) => e.codigo && e.codigo === codigoAtual.codigo) : [];

  function NivelCodigo() {
    if (!det || det.carregando) return <div className="pa-loading"><div className="pa-spinner" />Buscando atendimentos e demonstrativos...</div>;
    if (det.erro) return <div className="pa-erro">{det.erro}</div>;
    return (
      <>
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Exames do LIS com o código {codigoAtual.codigo} (valor calculado agora)</div>
          <div className="pa-tabela-scroll">
            <table className="pa-tabela">
              <thead><tr><th>Exame</th><th>Descrição</th><th className="num">CH</th><th className="num">Multiplicador</th><th className="num">Valor atual</th><th className="num">UNIMED</th><th className="num">Diferença</th><th className="num">CH correto</th><th className="num">Mult. correto</th><th>Último uso</th><th className="num">Último gravado</th></tr></thead>
              <tbody>
                {(exameDaTabela.length ? exameDaTabela : []).map((e) => (
                  <tr key={e.exame}>
                    <td><b>{e.exame}</b></td><td>{e.descricao}</td>
                    <td className="num">{dec(e.ch)}</td>
                    <td className="num">{dec(e.multiplicador, 4)}{e.mult_individual && <span className="tu-tag" title="Multiplicador individual do exame no plano">ind.</span>}</td>
                    <td className="num"><b>{dec(e.valor)}</b></td><td className="num">{dec(e.unimed)}</td>
                    <td className="num"><Diferenca v={e.diferenca} /></td>
                    <td className="num">{dec(e.ch_correto)}</td><td className="num">{dec(e.mult_correto, 4)}</td>
                    <td>{dataBR(e.ultimo_uso)}</td>
                    <td className="num">{e.valor_gravado != null && e.valor != null && Math.abs(e.valor_gravado - e.valor) > 0.009
                      ? <span className="tu-mais" title="O último atendimento gravou um valor diferente do cálculo atual (o ajuste só vale para atendimentos novos)">{dec(e.valor_gravado)}</span>
                      : dec(e.valor_gravado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Mês a mês — clique no mês para ver os atendimentos e as glosas</div>
          <div className="pa-tabela-scroll">
            <table className="pa-tabela">
              <thead><tr><th>Mês</th><th className="num">UNIMED pagou (local)</th><th className="num">UNIMED pagou (intercâmbio)</th><th className="num">LIS gravou</th><th className="num">Atendimentos</th><th className="num">Itens pagos</th><th className="num">Glosas 1705</th><th /></tr></thead>
              <tbody>
                {det.historico.map((h) => (
                  <tr key={h.mes} className="clicavel" onClick={() => entrar({ tipo: 'mes', mes: h.mes })}>
                    <td><b>{mesBR(h.mes)}</b></td>
                    <td className="num">{dec(h.unimed_local)}</td><td className="num">{dec(h.unimed_inter)}</td>
                    <td className="num">{h.lis != null && h.unimed_local != null && Math.abs(h.lis - h.unimed_local) > 0.009
                      ? <span className={h.lis > h.unimed_local ? 'tu-mais' : 'tu-menos'}>{dec(h.lis)}</span> : dec(h.lis)}</td>
                    <td className="num">{numero(h.atendimentos)}</td><td className="num">{numero(h.pagos)}</td>
                    <td className="num">{h.glosas_1705 ? <span className="tu-mais">{numero(h.glosas_1705)}</span> : '—'}</td>
                    <td className="tu-seta">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  function TabelaAtendimentos({ lista }) {
    if (!lista.length) return <p className="tu-sutil">Nenhum atendimento UNIMED com esse exame no mês.</p>;
    return (
      <div className="pa-tabela-scroll">
        <table className="pa-tabela">
          <thead><tr><th>Data</th><th>Protocolo</th><th>Paciente</th><th>Exame</th><th>Carteira</th><th className="num">LIS gravou</th><th className="num">UNIMED liberou</th><th className="num">Glosa</th><th>Glosas</th><th /></tr></thead>
          <tbody>
            {lista.map((a, i) => (
              <tr key={`${a.local}-${a.protocolo}-${a.exame}-${i}`} className="clicavel" onClick={() => entrar({ tipo: 'atendimento', atendimento: a })}>
                <td>{dataBR(a.data_pedido || a.data)}</td>
                <td className="ch-mono">{a.local}/{a.protocolo}</td>
                <td>{a.paciente}</td><td>{a.exame}</td>
                <td className="ch-mono">{a.carteira}{a.carteira && !/^0*144/.test(a.carteira) && <span className="tu-tag">interc.</span>}</td>
                <td className="num">{dec(a.valor)}</td>
                <td className="num">{a.pagamento ? dec(a.pagamento.liberado) : <span className="tu-sutil" title="Não encontrado nos demonstrativos importados (mesma carteira, até 5 dias)">—</span>}</td>
                <td className="num">{a.pagamento?.glosa ? <span className="tu-mais">{dec(a.pagamento.glosa)}</span> : '—'}</td>
                <td><Glosas codigos={a.pagamento?.glosas} /></td>
                <td className="tu-seta">›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function TabelaPagamentos({ lista, comAtendimento = true }) {
    if (!lista.length) return <p className="tu-sutil">Nenhum item desse código nos demonstrativos importados.</p>;
    return (
      <div className="pa-tabela-scroll">
        <table className="pa-tabela">
          <thead><tr><th>Realização</th><th>Demonstrativo</th><th>Guia UNIMED</th><th>Carteira</th><th className="num">Qtd</th><th className="num">Informado</th><th className="num">Processado</th><th className="num">Liberado</th><th className="num">Glosa</th><th>Glosas</th>{comAtendimento && <th>Atendimento no LIS</th>}</tr></thead>
          <tbody>
            {lista.map((p, i) => (
              <tr key={`${p.demonstrativo}-${p.guia}-${i}`} className={comAtendimento && p.atendimento ? 'clicavel' : ''}
                onClick={() => comAtendimento && p.atendimento && abrirAtendimento(p.atendimento)}>
                <td>{dataBR(p.data)}</td>
                <td className="ch-mono" title={`Emissão ${dataBR(p.emissao)} · protocolo ${p.protocolo} · lote ${p.lote}`}>{p.demonstrativo}<div className="ch-sub">pag. {dataBR(p.emissao)}</div></td>
                <td className="ch-mono">{p.guia}{p.senha && <div className="ch-sub">senha {p.senha}</div>}</td>
                <td className="ch-mono">{p.carteira}{p.intercambio && <span className="tu-tag">interc.</span>}</td>
                <td className="num">{dec(p.qtd, 0)}</td>
                <td className="num">{p.informado ? dec(p.informado) : <span className="tu-sutil" title="Guia enviada pelo portal, sem valor">portal</span>}</td>
                <td className="num">{dec(p.processado)}</td><td className="num">{dec(p.liberado)}</td>
                <td className="num">{p.glosa ? <span className="tu-mais">{dec(p.glosa)}</span> : '—'}</td>
                <td><Glosas codigos={p.glosas} /></td>
                {comAtendimento && <td>{p.atendimento
                  ? <span><span className="ch-mono">{p.atendimento.local}/{p.atendimento.protocolo}</span> · {p.atendimento.paciente}</span>
                  : <span className="tu-sutil">não localizado</span>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function NivelMes({ mes }) {
    if (!det || det.carregando) return <div className="pa-loading"><div className="pa-spinner" />Carregando...</div>;
    if (det.erro) return <div className="pa-erro">{det.erro}</div>;
    const atds = det.atendimentos.filter((a) => (a.data_pedido || a.data || '').startsWith(mes));
    const pags = det.pagamentos.filter((p) => (p.data || '').startsWith(mes));
    const glosados = pags.filter((p) => p.glosas);
    return (
      <>
        {glosados.length > 0 && (
          <div className="pa-card pa-card-full">
            <div className="pa-secao-titulo">Glosas do mês ({numero(glosados.length)})</div>
            <TabelaPagamentos lista={glosados} />
          </div>
        )}
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Atendimentos UNIMED no mês ({numero(atds.length)}) — clique para ver o atendimento</div>
          <TabelaAtendimentos lista={atds} />
        </div>
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Todos os itens pagos desse código no mês ({numero(pags.length)})</div>
          <TabelaPagamentos lista={pags} />
        </div>
      </>
    );
  }

  function NivelAtendimento({ atendimento: a }) {
    const pags = (det?.pagamentos || []).filter((p) => p.atendimento && p.atendimento.local === a.local && p.atendimento.protocolo === a.protocolo);
    const outrosExames = (det?.atendimentos || []).filter((x) => x.local === a.local && x.protocolo === a.protocolo);
    return (
      <>
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Atendimento</div>
          <div className="tu-ficha">
            <div><span>Protocolo</span><b className="ch-mono">{a.local}/{a.protocolo}</b></div>
            <div><span>Paciente</span><b>{a.paciente || '—'}</b></div>
            <div><span>Data do atendimento</span><b>{dataBR(a.data)}</b></div>
            <div><span>Data do pedido (guia)</span><b>{dataBR(a.data_pedido)}</b></div>
            <div><span>Carteira</span><b className="ch-mono">{a.carteira || '—'}</b></div>
            <div><span>Autorização</span><b className="ch-mono">{a.autorizacao || '—'}</b></div>
          </div>
        </div>
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">Exames desse atendimento com o código {codigoAtual?.codigo}</div>
          <div className="pa-tabela-scroll">
            <table className="pa-tabela">
              <thead><tr><th>Exame</th><th>Situação</th><th className="num">Valor gravado no LIS</th></tr></thead>
              <tbody>{outrosExames.map((x, i) => <tr key={i}><td>{x.exame}</td><td>{x.situacao}</td><td className="num">{dec(x.valor)}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
        <div className="pa-card pa-card-full">
          <div className="pa-secao-titulo">O que a UNIMED pagou desse atendimento (código {codigoAtual?.codigo})</div>
          {pags.length ? <TabelaPagamentos lista={pags} comAtendimento={false} />
            : <p className="tu-sutil">Nenhum item localizado nos demonstrativos importados (a ligação é pela carteira e data com até 5 dias de diferença). Pode estar em análise, não ter sido faturado ou estar num demonstrativo ainda não importado.</p>}
        </div>
      </>
    );
  }

  function NivelMesGlobal({ mes }) {
    const linhas = (glosasMes || []).filter((r) => r.mes === mes);
    const porCodigo = new Map((dados?.exames || []).filter((e) => e.codigo).map((e) => [e.codigo, e]));
    return (
      <div className="pa-card pa-card-full">
        <div className="pa-secao-titulo">Códigos com glosa em {mesBR(mes)} — clique para abrir o código nesse mês</div>
        <div className="pa-tabela-scroll">
          <table className="pa-tabela">
            <thead><tr><th>Código</th><th>Procedimento</th><th className="num">Itens pagos</th><th className="num">Itens glosados</th><th className="num">Glosas 1705</th><th className="num">Valor 1705</th><th className="num">Outras glosas (valor)</th><th>Códigos de glosa</th><th className="num">LIS hoje</th><th className="num">UNIMED</th><th /></tr></thead>
            <tbody>
              {linhas.map((r) => {
                const e = porCodigo.get(r.codigo);
                return (
                  <tr key={r.codigo} className="clicavel" onClick={() => { entrar({ tipo: 'codigo', codigo: r.codigo, exame: e?.exame }); setTrilha((t) => [...t, { tipo: 'mes', mes }]); }}>
                    <td className="ch-mono">{r.codigo}</td><td>{r.descricao}</td>
                    <td className="num">{numero(r.itens)}</td><td className="num">{numero(r.glosados)}</td>
                    <td className="num">{r.glosas_1705 ? <span className="tu-mais">{numero(r.glosas_1705)}</span> : '—'}</td>
                    <td className="num">{r.valor_1705 ? moeda(r.valor_1705) : '—'}</td>
                    <td className="num">{r.valor_outras ? moeda(r.valor_outras) : '—'}</td>
                    <td><Glosas codigos={r.codigos_glosa} /></td>
                    <td className="num">{e ? dec(e.valor) : '—'}</td><td className="num">{e ? dec(e.unimed) : '—'}</td>
                    <td className="tu-seta">›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------ raiz
  return (
    <div className="pa-root tu-root">
      <div className="pa-header">
        <div>
          <h1>Tabela UNIMED × o que a UNIMED paga</h1>
          <p>
            {dados ? <>Plano <b>{dados.plano.convenio}/{dados.plano.plano}</b> · tabela <b>{dados.plano.tabela_nome || dados.plano.tabela}</b> · multiplicador <b>{dec(dados.plano.multiplicador, 4)}</b> · lido do LIS agora</> : 'Carregando...'}
          </p>
        </div>
        <div className="ch-acoes">
          <button className="pa-btn pa-btn-secundario" onClick={carregar} disabled={carregando}>{carregando ? 'Atualizando...' : '↻ Atualizar'}</button>
          <label className={`pa-btn ${importando ? 'tu-desab' : ''}`} title="XML analítico do demonstrativo de análise de conta, baixado do portal SGU-Card">
            {importando ? 'Importando...' : 'Importar demonstrativo (XML)'}
            <input type="file" accept=".xml" multiple hidden onChange={importar} disabled={importando} />
          </label>
        </div>
      </div>

      {erro && <div className="pa-erro">{erro}</div>}
      {importacao && (
        <div className="ch-info">
          {importacao.map((r, i) => (
            <div key={i} className={r.ok ? 'ch-ok' : 'ch-alerta'}>
              {r.ok ? `✓ ${r.arquivo}: demonstrativo ${r.demonstrativo} (${dataBR(r.emissao)}), ${numero(r.itens)} itens` : `⚠ ${r.arquivo}: ${r.erro}`}
            </div>
          ))}
        </div>
      )}

      {trilha.length > 0 && (
        <div className="tu-trilha">
          <button onClick={() => voltarPara(0)}>Tabela UNIMED</button>
          {trilha.map((no, i) => (
            <React.Fragment key={i}>
              <span>›</span>
              {i === trilha.length - 1 ? <b>{rotulo(no)}</b> : <button onClick={() => voltarPara(i + 1)}>{rotulo(no)}</button>}
            </React.Fragment>
          ))}
          <button className="tu-voltar" onClick={() => voltarPara(trilha.length - 1)}>← Voltar</button>
        </div>
      )}

      {topo?.tipo === 'codigo' && <NivelCodigo />}
      {topo?.tipo === 'mes' && <NivelMes mes={topo.mes} />}
      {topo?.tipo === 'atendimento' && <NivelAtendimento atendimento={topo.atendimento} />}
      {topo?.tipo === 'mesGlobal' && <NivelMesGlobal mes={topo.mes} />}

      {!topo && dados && (
        <>
          <div className="ch-barra">
            <div className="ch-abas">
              <button className={`pa-chip ${visao === 'exames' ? 'ativo' : ''}`} onClick={() => setVisao('exames')}>Por exame</button>
              <button className={`pa-chip ${visao === 'meses' ? 'ativo' : ''}`} onClick={() => setVisao('meses')}>Glosas por mês</button>
              <button className={`pa-chip ${visao === 'demonstrativos' ? 'ativo' : ''}`} onClick={() => setVisao('demonstrativos')}>Demonstrativos importados ({numero(dados.demonstrativos.length)})</button>
            </div>
          </div>

          {visao === 'exames' && (
            <>
              <div className="pa-filtros">
                <div className="pa-filtros-linha tu-filtros">
                  <div className="ch-abas">
                    {[['divergentes', `Divergentes (${numero(contagem.mais + contagem.menos)})`], ['mais', `${SITUACAO.mais.label} (${numero(contagem.mais)})`],
                      ['menos', `${SITUACAO.menos.label} (${numero(contagem.menos)})`], ['ok', `${SITUACAO.ok.label} (${numero(contagem.ok)})`],
                      ['sem', `${SITUACAO.sem.label} (${numero(contagem.sem)})`], ['todos', 'Todos']].map(([k, l]) => (
                      <button key={k} className={`pa-chip ${filtro === k ? 'ativo' : ''}`} onClick={() => setFiltro(k)} title={SITUACAO[k]?.desc}>
                        {SITUACAO[k] && <i className="pa-ponto" style={{ color: SITUACAO[k].cor, opacity: 1 }} />}{l}
                      </button>
                    ))}
                  </div>
                  <label className="tu-check"><input type="checkbox" checked={soUsados} onChange={(e) => setSoUsados(e.target.checked)} /> Só exames usados em guia UNIMED nos últimos 12 meses</label>
                  <input className="ch-busca" placeholder="Buscar exame, descrição ou código TUSS" value={busca} onChange={(e) => setBusca(e.target.value)} />
                </div>
              </div>
              <div className="pa-card pa-card-full">
                <div className="pa-tabela-scroll">
                  <table className="pa-tabela">
                    <thead><tr>
                      <Th campo="exame">Exame</Th><Th campo="descricao">Descrição</Th><Th campo="codigo">TUSS</Th>
                      <Th campo="ch" num>CH</Th><Th campo="multiplicador" num>Mult.</Th><Th campo="valor" num>Valor LIS</Th>
                      <Th campo="unimed" num>UNIMED</Th><Th campo="diferenca" num>Diferença</Th><Th campo="ch_correto" num>CH correto</Th>
                      <Th campo="mult_correto" num>Mult. correto</Th><Th campo="uso_12m" num>Uso 12m</Th><Th campo="ultimo_uso">Último uso</Th>
                      <Th campo="glosas_1705" num><span title="Do código TUSS nos últimos 12 meses (repete em cada exame de apoio com o mesmo código)">Glosas 1705 do código</span></Th><Th campo="glosas_1705_valor" num><span title="Valor glosado por diferença de tabela no código TUSS, últimos 12 meses">Valor glosado do código</span></Th><th />
                    </tr></thead>
                    <tbody>
                      {exames.map((e) => (
                        <tr key={e.exame} className="clicavel" onClick={() => e.codigo && entrar({ tipo: 'codigo', codigo: e.codigo, exame: e.exame })}>
                          <td><i className="pa-ponto" style={{ color: SITUACAO[e.situacao].cor, opacity: 1 }} title={SITUACAO[e.situacao].label} /><b>{e.exame}</b></td>
                          <td>{e.descricao}{e.procedimento && <div className="ch-sub">{e.procedimento}</div>}</td>
                          <td className="ch-mono">{e.codigo || '—'}</td>
                          <td className="num">{dec(e.ch)}</td>
                          <td className="num">{dec(e.multiplicador, 4)}{e.mult_individual && <span className="tu-tag">ind.</span>}</td>
                          <td className="num"><b>{dec(e.valor)}</b></td>
                          <td className="num" title={e.unimed != null ? `${e.unimed_obs} pagamentos nos 90 dias até ${dataBR(e.unimed_ate)}${e.unimed_inter != null ? ` · intercâmbio ${dec(e.unimed_inter)}` : ''}` : ''}>{dec(e.unimed)}</td>
                          <td className="num"><Diferenca v={e.diferenca} /></td>
                          <td className="num">{dec(e.ch_correto)}</td><td className="num">{dec(e.mult_correto, 4)}</td>
                          <td className="num">{numero(e.uso_12m)}</td><td>{dataBR(e.ultimo_uso)}</td>
                          <td className="num">{e.glosas_1705 ? numero(e.glosas_1705) : '—'}</td>
                          <td className="num">{e.glosas_1705_valor ? moeda(e.glosas_1705_valor) : '—'}</td>
                          <td className="tu-seta">{e.codigo ? '›' : ''}</td>
                        </tr>
                      ))}
                      {!exames.length && <tr><td colSpan={15} className="tu-sutil">Nenhum exame neste filtro.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <p className="tu-nota">
                  Valor LIS = CH × multiplicador (o individual do exame no plano, se houver; senão o do plano), calculado agora a partir do cadastro.
                  UNIMED = valor mais frequente pago por esse código nas carteiras da UNIMED Divinópolis nos últimos 90 dias de dados.
                  O envio para a UNIMED usa o valor gravado no atendimento quando ele foi cadastrado: ajustar o cadastro corrige os atendimentos novos.
                </p>
              </div>
            </>
          )}

          {visao === 'meses' && (
            <div className="pa-card pa-card-full">
              <div className="pa-secao-titulo">Glosas por mês de realização — clique no mês para ver os códigos</div>
              <div className="pa-tabela-scroll">
                <table className="pa-tabela">
                  <thead><tr><th>Mês</th><th className="num">Códigos com glosa</th><th className="num">Itens glosados</th><th className="num">Glosas 1705</th><th className="num">Valor 1705</th><th className="num">Outras glosas (valor)</th><th /></tr></thead>
                  <tbody>
                    {meses.map((m) => (
                      <tr key={m.mes} className="clicavel" onClick={() => entrar({ tipo: 'mesGlobal', mes: m.mes })}>
                        <td><b>{mesBR(m.mes)}</b></td><td className="num">{numero(m.codigos)}</td>
                        <td className="num">{numero(m.glosados)}</td><td className="num">{numero(m.glosas_1705)}</td>
                        <td className="num">{moeda(m.valor_1705)}</td><td className="num">{moeda(m.valor_outras)}</td>
                        <td className="tu-seta">›</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {visao === 'demonstrativos' && (
            <div className="pa-card pa-card-full">
              <div className="pa-secao-titulo">Demonstrativos importados</div>
              <div className="pa-tabela-scroll">
                <table className="pa-tabela">
                  <thead><tr><th>Demonstrativo</th><th>Pagamento</th><th>Realizações de</th><th>até</th><th className="num">Itens</th><th>Importado em</th></tr></thead>
                  <tbody>
                    {dados.demonstrativos.map((d) => (
                      <tr key={d.demonstrativo}>
                        <td className="ch-mono">{d.demonstrativo}</td><td>{dataBR(d.emissao)}</td><td>{dataBR(d.de)}</td><td>{dataBR(d.ate)}</td>
                        <td className="num">{numero(d.itens)}</td><td>{d.importado_em ? new Date(d.importado_em).toLocaleString('pt-BR') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="tu-nota">Importe o XML analítico de cada demonstrativo novo (portal SGU-Card › Demonstrativos). Reimportar o mesmo demonstrativo substitui os itens dele.</p>
            </div>
          )}
        </>
      )}
      {carregando && !dados && <div className="pa-loading"><div className="pa-spinner" />Lendo a tabela do LIS e os demonstrativos...</div>}
    </div>
  );
}
